import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data);
      toast.success("If the account exists, a reset link has been sent.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Forgot your password?" subtitle="Enter your email and we'll send you a reset link.">
      {!sent ? (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label>Work email</Label>
            <Input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                   className="mt-1.5 bg-white" data-testid="forgot-email" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-brand-aqua hover:bg-brand-aqua-hover text-white h-11" data-testid="forgot-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Send reset link
          </Button>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm">
            <CheckCircle2 className="w-4 h-4" /> Request received
          </div>
          {sent.dev_reset_link && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
              <div className="text-xs text-amber-800 font-medium">Dev mode — email delivery not configured</div>
              <div className="text-xs text-slate-600">Use this link to reset (valid 30 min):</div>
              <a href={sent.dev_reset_link} className="text-xs font-mono text-brand-aqua-hover break-all underline" data-testid="dev-reset-link">
                {sent.dev_reset_link}
              </a>
            </div>
          )}
        </div>
      )}
      <Link to="/login" className="mt-4 inline-flex items-center text-xs text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-3 h-3 mr-1" /> Back to sign in
      </Link>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    if (!token) return toast.error("Missing reset token");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. Sign in with your new password.");
      setTimeout(() => navigate("/login"), 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password of at least 8 characters.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label>New password</Label>
          <Input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                 className="mt-1.5 bg-white" data-testid="reset-password" />
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
                 className="mt-1.5 bg-white" data-testid="reset-confirm" />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-brand-aqua hover:bg-brand-aqua-hover text-white h-11" data-testid="reset-submit">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <KeyRound className="w-4 h-4 mr-2" /> Update password
        </Button>
      </form>
      <Link to="/login" className="mt-4 inline-flex items-center text-xs text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-3 h-3 mr-1" /> Back to sign in
      </Link>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-brand-navy">
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy-hover to-brand-aqua/20" />
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-brand-aqua flex items-center justify-center font-heading font-bold">F</div>
          <div>
            <div className="font-heading text-xl font-semibold">FiguraidOS</div>
            <div className="text-xs text-slate-300">Internal Operations Platform</div>
          </div>
        </div>
        <div className="relative text-xs text-slate-400">© 2026 Figuraid Ltd. Lagos, Nigeria.</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <h2 className="font-heading text-2xl font-semibold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
